import { useRef } from 'react'
import { AlertCircle, Loader2, Paperclip, X } from 'lucide-react'
import type { AttachmentDraft } from '../../hooks/useAttachmentUpload'

interface AttachmentDropzoneProps {
  drafts: AttachmentDraft[]
  onAddFiles: (files: File[]) => void
  onRemove: (localId: string) => void
  disabled?: boolean
  /** Restringe o seletor de arquivos do SO (ex.: `"image/*"`). Não é
   * validação de verdade por si só — apenas UX, o filtro real acontece em
   * `useAttachmentUpload`/no backend. Sem valor = sem restrição. */
  accept?: string
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Bloco compartilhado entre Ocorrências (`MessageEditor`/`NewOccurrenceForm`)
 * e Problemas Reportados. Layout horizontal compacto: botão de clipe + chips
 * dos anexos lado a lado com flex-wrap. Cada chip mostra ícone de estado
 * (uploading/ready/error) + nome (truncado) + tamanho + botão remover.
 *
 * Não tem container próprio — espera ser colocado dentro do footer do
 * editor onde o botão "Enviar" também vive, ficando tudo na mesma linha.
 */
export function AttachmentDropzone({
  drafts,
  onAddFiles,
  onRemove,
  disabled,
  accept,
}: AttachmentDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handlePickClick = () => {
    inputRef.current?.click()
  }

  const handleFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    if (files.length > 0) onAddFiles(files)
    e.target.value = ''
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={handleFilesPicked}
      />

      <button
        type="button"
        onClick={handlePickClick}
        disabled={disabled}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted transition hover:bg-surface-muted hover:text-body/80 disabled:opacity-50"
        title="Anexar arquivos"
        aria-label="Anexar arquivos"
      >
        <Paperclip className="h-4 w-4" />
      </button>

      {drafts.map((d) => (
        <AttachmentChip
          key={d.localId}
          draft={d}
          onRemove={() => onRemove(d.localId)}
        />
      ))}
    </div>
  )
}

interface AttachmentChipProps {
  draft: AttachmentDraft
  onRemove: () => void
}

function AttachmentChip({ draft: d, onRemove }: AttachmentChipProps) {
  const isUploading = d.status === 'uploading'
  const isError = d.status === 'error'

  return (
    <span
      className={`inline-flex max-w-[200px] items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${
        isError
          ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
          : 'border-border-subtle bg-surface-muted text-body/80'
      }`}
      title={
        isError
          ? `${d.file.name} — ${d.errorMessage ?? 'erro'}`
          : `${d.file.name} · ${formatSize(d.file.size)}${
              isUploading ? ` · ${d.progress}%` : ''
            }`
      }
    >
      {isUploading && (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-blue-600 dark:text-blue-400" />
      )}
      {!isUploading && !isError && (
        <Paperclip className="h-3 w-3 shrink-0 text-muted" />
      )}
      {isError && <AlertCircle className="h-3 w-3 shrink-0 text-red-600 dark:text-red-400" />}
      <span className="truncate">{d.file.name}</span>
      <button
        type="button"
        onClick={onRemove}
        className="-mr-1 ml-0.5 shrink-0 rounded-full p-0.5 text-subtle hover:bg-surface-raised hover:text-body/80"
        aria-label="Remover anexo"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}
